<template>
  <pui-common-input-tag
    ref="inputTag"
    :items="myItems"
    :value="value"
    :duplicateItemIndexes="existingValueItemIndexes"
    :placeholder="$t('dam.files.search.input.placeholder')"
    :displayDuplicateErrors="displayDuplicateErrors"
    :editItemValue="editItemValue"
    :editItemIndex="editItemIndex"
    :singleLine="singleLine"
    :singleLineHeight="32"
    :getSearchItemClass="getItemClass"
    :hideClearButton="!myItems.length && !value"
    :hasError="!inputAttributeExists || !editItemAttributeExists"
    @input="onInput"
    @enter="onEnter"
    @removeItem="onRemoveItem"
    @blur="onBlur"
    @clearSelection="onClearSelection"
    @clickItem="onClickItem"
    @dblClickItem="onDblClickItem"
    @editItemInput="onEditItemInput"
    @changeEditItemIndex="onChangeEditItemIndex"
    @blurEditItemInput="onBlurEditItemInput"
    @enterEditItem="onEnterEditItem"
    class="file-search-input__root"
  >
    <!-- add our search icon -->
    <template v-slot:rightIcon>
      <pui-button
        @click="onSearchButtonClick"
        class="file-search-input__icon-search"
        picto="mdi-magnify"
        variant="primary"
      />
    </template>

    <!-- custom itemContent to render the 'value' instead -->
    <template v-slot:itemContent="{ className, item }">
      <div :class="className">
        {{ item.value }}
      </div>
    </template>

    <template #hiddenItemsCount="{ hiddenItemsCount }">
      {{
        hiddenItemsCount
          ? $tc('dam.search.input.hiddenItemsCount', hiddenItemsCount, { hiddenItemsCount })
          : null
      }}
    </template>
  </pui-common-input-tag>
</template>

<script>
import { SearchType } from '../../../constants/search';
let searchItemId = 0;

export default {
  props: {
    /**
     * Array of search items to use. We need this prop so
     * that way we can manipulate our own array, emit it to the parent,
     * but also receive any updates via watch
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * If the InputTag should only display a single line
     */
    singleLine: {
      type: Boolean,
      required: false,
    },
    /**
     * Checks if the input value corresponds to an attribute,
     * if that attribute exists
     */
    isSearchItemValid: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      value: '',
      inputAttributeExists: true,
      myItems: [],
      displayDuplicateErrors: false,
      displayDuplicatesTimeout: null,
      editItemIndex: -1,
      editItemAttributeExists: true,
    };
  },
  computed: {
    /**
     * @returns {Boolean} if the main input value can be used
     */
    hasValue() {
      return !!this.value && !!this.value.trim();
    },
    /**
     * @returns {Boolean} if the edit item input value can be used
     */
    editItemHasValue() {
      return !!this.editItemValue && !!this.editItemValue.trim();
    },
    /**
     * @returns {Boolean} if the main input value can be used, and does
     * not already exist in the items array
     */
    canAddItem() {
      return (
        this.hasValue && this.existingValueItemIndexes.length === 0 && this.inputAttributeExists
      );
    },
    /**
     * @returns {Boolean} if the edit item input value can be used, and does
     * not already exist in the items array
     */
    canValidateEditingItem() {
      return (
        this.editItemHasValue &&
        this.existingValueItemIndexes.length === 0 &&
        this.editItemAttributeExists
      );
    },
    /**
     * @returns {Array<Number>} the indexes of items that have the same
     * value as the edit item input, or the main input
     */
    existingValueItemIndexes() {
      return (
        this.myItems
          .map((item, index) => {
            if (index === this.editItemIndex) {
              return -1;
            }
            // Check dup against an editing item
            if (this.editItemIndex !== -1) {
              return item.value === this.editItemValue ? index : -1;
            }

            // Check dup against the main input
            return item.value === this.value ? index : -1;
          })
          // Remove indexes that were not duplicates
          .filter(dupIndex => dupIndex !== -1)
      );
    },
    /**
     * @returns {String} the value of the edit item
     */
    editItemValue() {
      return this.editItemIndex !== -1 && this.myItems[this.editItemIndex]
        ? this.myItems[this.editItemIndex].value
        : '';
    },
  },
  watch: {
    /**
     * Updates our data myItems to match the prop items
     *
     * @param {object} nextValue - the new value of search items
     * @returns {void}
     */
    items(nextValue) {
      this.myItems = nextValue;
    },
  },
  methods: {
    /**
     * Emits the 'search' event
     *
     * @param {MouseEvent} - the click on the button that caused the search
     * @returns {void}
     */
    onSearchButtonClick(event) {
      this.$emit('searchClicked', event);
    },
    /**
     * Adds an item to the 'myItems' array and
     * resets the main input value
     *
     * @returns {void}
     */
    addItem() {
      const value = this.value;
      this.myItems.push({
        id: searchItemId++,
        value,
      });
      this.$emit('addValue', value, [...this.myItems]);
      this.value = '';
    },
    /**
     * Displays the duplicate items for a set amount
     * of time, and then hides them
     *
     * @returns {void}
     */
    flashDuplicates() {
      this.displayDuplicateErrors = true;
      this.displayDuplicatesTimeout = setTimeout(() => {
        this.displayDuplicateErrors = false;
      }, 500);
    },
    // Event handlers
    /**
     * Updates the main input value
     *
     * @param {KeyboardEvent} event - the keyboard event in the input
     * @returns {void}
     */
    onInput(event) {
      const value = event.target.value;
      this.value = value;

      this.inputAttributeExists = this.isSearchItemValid(value);
    },
    /**
     * Handles 'enter' on the main input.
     * Eithers adds the input if valid, or emits the 'enter' event
     *
     * @param {KeyboardEvent} event - the keyboard event in the input
     * @returns {void}
     */
    onEnter(event) {
      this.flashDuplicates();

      if (this.hasValue && this.canAddItem) {
        this.addItem();
      } else if (
        !this.hasValue &&
        this.$refs.inputTag.getFocusedItemIndex() === this.myItems.length
      ) {
        this.$emit('enter', event);
      }
    },
    /**
     * Removes the item and focuses the item that will take its place
     *
     * @param {object} item - the item to remove
     * @param {number} itemIndex - the index of the item
     * @param {MouseEvent|KeyboardEvent} event - the click or delete key event
     * @returns {void}
     */
    onRemoveItem(item, itemIndex, event) {
      const willDeleteLast = itemIndex === this.myItems.length - 1;
      const willHaveMoreItems = this.myItems.length > 1;
      this.myItems.splice(itemIndex, 1);

      // Focus the item that took its place
      // if deleting last item, focus the item n-1
      this.$nextTick(() => {
        this.$refs.inputTag.focusItem(
          willDeleteLast && willHaveMoreItems ? itemIndex - 1 : itemIndex
        );
      });

      this.$emit('removeItem', item, [...this.myItems]);
    },
    /**
     * Adds the item if valid, or emits blur
     *
     * @param {MouseEvent} event - the click that caused the blur
     * @returns {void}
     */
    onBlur(event) {
      if (this.hasValue && this.canAddItem) {
        this.addItem();
      } else if (!this.hasValue) {
        this.$emit('blur', event);
      }
    },
    /**
     * Clears the items and the main input
     *
     * @param {MouseEvent} event - the click on what caused to clear
     * the selection
     * @returns {void}
     */
    onClearSelection(event) {
      this.myItems = [];
      this.value = '';
      this.inputAttributeExists = true;
      this.editItemAttributeExists = true;

      this.$emit('clearSelection', event);
    },
    /**
     * Focuses the clicked item
     *
     * @param {object} item - the item that was clicked
     * @param {number} itemIndex - the index of the item
     * @param {MouseEvent} event - the click event
     * @returns {void}
     */
    onClickItem(item, itemIndex, event) {
      this.$refs.inputTag.focusItem(itemIndex);
    },
    /**
     * Triggers edit mode for the item
     *
     * @param {object} item - the item was clicked
     * @param {number} itemIndex - the index of the item
     * @param {MouseEvent} event - the click event
     * @returns {void}
     */
    onDblClickItem(item, itemIndex, event) {
      this.onChangeEditItemIndex(itemIndex, event);
    },
    /**
     * Updates the edit item's value
     *
     * @param {object} item - the item that was edited
     * @param {number} itemIndex - the index of the item
     * @param {KeyboardEvent} event - the keyboard event
     * @returns {void}
     */
    onEditItemInput(event) {
      const value = event.target.value;
      this.myItems[this.editItemIndex].value = value;

      this.editItemAttributeExists = this.isSearchItemValid(value);
    },
    /**
     * Updates the editing item
     *
     * @param {object} item - the item to remove
     * @param {number} itemIndex - the index of the item
     * @param {MouseEvent} event - the click event
     * @returns {void}
     */
    onChangeEditItemIndex(nextIndex, event) {
      // If the current edit item is an attribute-value form, but does not map
      // to an attribute, prevent changing edit item
      if (!this.editItemAttributeExists) {
        return;
      }

      // If we want to edit the last item, remove it
      // and set the input's value as the item's value
      if (nextIndex === this.myItems.length - 1) {
        const item = this.myItems[nextIndex];
        const value = item.value;
        this.myItems.splice(nextIndex, 1);
        this.value = value;

        this.$nextTick(() => {
          this.$refs.inputTag.focus();
        });

        this.$emit('removeItem', item, [...this.myItems]);
      } else {
        // Otherwise just mark that item as being edited
        this.editItemIndex = nextIndex;
        this.$nextTick(() => {
          this.$refs.inputTag.focusItem(nextIndex);
        });
      }
    },
    /**
     * Handles the edit item blur. Either leaves it as it is
     * if invalid, cancels the edit (without changing the value),
     * or removes the item if empty
     *
     * @param {FocusEvent|MouseEvent} event - the click or focus event
     * @returns {void}
     */
    onBlurEditItemInput(event) {
      const prevEditItemIndex = this.editItemIndex;

      // Prevent validating if value exists in another item
      if (
        prevEditItemIndex === -1 ||
        (this.editItemHasValue && !this.canValidateEditingItem) ||
        !this.editItemAttributeExists
      ) {
        return;
      }

      // Exit edit mode by removing the edit index
      this.editItemIndex = -1;

      // When an item was blurred, if the input is empty, remove it
      const item = this.myItems[prevEditItemIndex];
      if (item.value === '') {
        this.onRemoveItem(this.myItems[prevEditItemIndex], prevEditItemIndex, event);
      }
    },
    /**
     * Handles 'enter' on the edit item. Either leaves it as it is
     * if invalid, cancels the edit (without changing the value),
     * or removes the item if empty
     *
     * @returns {void}
     */
    onEnterEditItem() {
      this.flashDuplicates();

      const prevEditItemIndex = this.editItemIndex;

      // Prevent validating if value exists in another item
      if (
        prevEditItemIndex === -1 ||
        (this.editItemHasValue && !this.canValidateEditingItem) ||
        !this.editItemAttributeExists
      ) {
        return;
      }

      // Exit edit mode by removing the edit index
      this.editItemIndex = -1;

      this.$nextTick(() => {
        if (prevEditItemIndex === this.myItems.length - 1) {
          this.$refs.inputTag.focus();
        } else {
          this.$refs.inputTag.focusItem(prevEditItemIndex);
        }
      });
    },
    /**
     * @param {Any} item - the search item
     * @param {Number} itemIndex - the index of the item
     * @returns {object} additional classes for a search item
     */
    getItemClass(item, itemIndex) {
      return {
        'file-search-input__search_item': true,
        [`search-type-${item._searchSourceType || SearchType.DEFAULT}`]: true,
      };
    },
  },
};
</script>
